import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Alert, Box, Button, CircularProgress, Snackbar, Tooltip } from '@mui/material';
import { useState } from 'react';
import { exportAppCopyJson } from './appCopyCatalogClient';

type AppCopyExportButtonProps = {
  readonly onExport?: () => Promise<void>;
};

export function AppCopyExportButton({ onExport = exportAppCopyJson }: AppCopyExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleExport = async (): Promise<void> => {
    setIsExporting(true);
    setErrorMessage(null);
    try {
      await onExport();
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        throw error;
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Tooltip
        title={
          <span data-copy-export-ignore="true">
            Export current page UI/UX text as JSON
          </span>
        }
      >
        <span data-copy-export-ignore="true">
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            aria-label="Export current page UI text as JSON"
            disabled={isExporting}
            startIcon={isExporting ? <CircularProgress size={14} color="inherit" /> : <FileDownloadOutlinedIcon />}
            onClick={() => {
              void handleExport();
            }}
            sx={{
              minWidth: { xs: 32, sm: 'auto' },
              px: { xs: 1, sm: 1.25 },
              whiteSpace: 'nowrap',
              '& .MuiButton-startIcon': {
                ml: 0,
                mr: { xs: 0, sm: 1 },
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {isExporting ? 'Exporting…' : 'Export page text'}
            </Box>
          </Button>
        </span>
      </Tooltip>
      <Snackbar
        open={errorMessage !== null}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setErrorMessage(null)}
      >
        <Alert severity="error" variant="filled" onClose={() => setErrorMessage(null)}>
          Could not export UI text. {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
